import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CompareProvider } from "@/contexts/CompareContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { QuoteProvider } from "@/contexts/QuoteContext";
import { ResellerPreviewProvider } from "@/contexts/ResellerPreviewContext";
import { GridSettingsProvider } from "@/contexts/GridSettingsContext";
import { usePageTracking } from "@/hooks/usePageTracking";

const CatalogPage = lazy(() => import("./pages/CatalogPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const QuoteViewPage = lazy(() => import("./pages/QuoteViewPage"));

const queryClient = new QueryClient();

function AppRoutes() {
  usePageTracking();
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
      <Routes>
        <Route path="/" element={<CatalogPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/devis/:quoteId" element={<QuoteViewPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <AuthProvider>
          <ResellerPreviewProvider>
            <GridSettingsProvider>
              <CompareProvider>
                <QuoteProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter>
                    <AppRoutes />
                  </BrowserRouter>
                </QuoteProvider>
              </CompareProvider>
            </GridSettingsProvider>
          </ResellerPreviewProvider>
        </AuthProvider>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
